import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import PageLayout from '../components/PageLayout';
import { Calendar, MapPin, Building2, ArrowRight } from 'lucide-react';
import { useContactModal } from '../contexts/ContactModalContext';
import { Helmet } from 'react-helmet-async';

interface Project {
  id: string;
  title: string;
  description: string;
  image_url: string;
  category: string;
  completion_date: string;
  location: string;
  featured: boolean;
  status: 'ongoing' | 'completed' | 'upcoming';
}

const Projects = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [categories, setCategories] = useState<string[]>([]);
  const [featuredProjects, setFeaturedProjects] = useState<Project[]>([]);
  const { openModal } = useContactModal();

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const { data, error } = await supabase
          .from('projects')
          .select('*')
          .order('created_at', { ascending: false });

        if (error) throw error;

        if (data) {
          const allProjects = data as Project[];
          setProjects(allProjects);
          setFeaturedProjects(allProjects.filter(p => p.featured));

          // Extract unique categories
          const uniqueCategories = Array.from(
            new Set(allProjects.map(project => project.category))
          );
          setCategories(['all', ...uniqueCategories]);
        }
      } catch (error) {
        console.error('Error fetching projects:', error);
      }
    };

    fetchProjects();
  }, []);

  const filteredProjects = selectedCategory === 'all'
    ? projects
    : projects.filter(project => project.category === selectedCategory);

  return (
    <PageLayout
      title="Our Projects"
      subtitle="Explore our portfolio of exceptional homes and spaces"
    >
      <Helmet>
        <title>Portfolio of Custom Homes in Kerala | Master Home Projects</title>
        <meta name="description" content="View our portfolio of premium custom homes and luxury villas built by Master Home across Kannur, Mattannur, and Iritty, Kerala." />
        <link rel="canonical" href="https://masterhome.co.in/projects" />
      </Helmet>

      {/* Deep Dive Case Studies (SEO Money Pages) */}
      <section className="mb-24 mt-8">
        <h2 className="text-3xl font-bold text-center mb-12">In-Depth Case Studies</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <Link to="/projects/luxury-villa-kannur" className="group rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all border border-gray-100">
            <div className="aspect-w-16 aspect-h-10">
              <img src="/custom-home-construction-kannur.jpg" alt="Luxury Villa Kannur" className="object-cover w-full h-full group-hover:scale-110 transition-transform duration-500" />
            </div>
            <div className="p-6 bg-white">
              <h3 className="text-xl font-bold mb-2 group-hover:text-primary-600 transition-colors">Luxury Villa Construction in Kannur</h3>
              <p className="text-gray-600 mb-4 line-clamp-2">A stunning example of modern Kerala architecture meeting contemporary luxury.</p>
              <span className="text-primary-600 font-bold flex items-center gap-2">Read Case Study <ArrowRight size={16} /></span>
            </div>
          </Link>
          <Link to="/projects/modern-home-iritty" className="group rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all border border-gray-100">
            <div className="aspect-w-16 aspect-h-10">
              <img src="/house-renovation-iritty.jpg" alt="Modern Home Iritty" className="object-cover w-full h-full group-hover:scale-110 transition-transform duration-500" />
            </div>
            <div className="p-6 bg-white">
              <h3 className="text-xl font-bold mb-2 group-hover:text-primary-600 transition-colors">Modern Home Construction in Iritty</h3>
              <p className="text-gray-600 mb-4 line-clamp-2">A contemporary architectural masterpiece built securely on Kerala's challenging terrain.</p>
              <span className="text-primary-600 font-bold flex items-center gap-2">Read Case Study <ArrowRight size={16} /></span>
            </div>
          </Link>
          <Link to="/projects/contemporary-house-taliparamba" className="group rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all border border-gray-100">
            <div className="aspect-w-16 aspect-h-10">
              <img src="/villa-builders-taliparamba.jpg" alt="Contemporary House Taliparamba" className="object-cover w-full h-full group-hover:scale-110 transition-transform duration-500" />
            </div>
            <div className="p-6 bg-white">
              <h3 className="text-xl font-bold mb-2 group-hover:text-primary-600 transition-colors">Contemporary House in Taliparamba</h3>
              <p className="text-gray-600 mb-4 line-clamp-2">A perfect blend of Kerala tradition and cutting-edge contemporary design.</p>
              <span className="text-primary-600 font-bold flex items-center gap-2">Read Case Study <ArrowRight size={16} /></span>
            </div>
          </Link>
        </div>
      </section>

      {/* Featured Projects */}
      {featuredProjects.length > 0 && (
        <section className="mb-24">
          <h2 className="text-3xl font-bold text-center mb-12">Featured Projects</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {featuredProjects.map((project) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="group relative overflow-hidden rounded-2xl bg-white shadow-xl"
              >
                <div className="aspect-w-16 aspect-h-9 overflow-hidden">
                  <img
                    src={project.image_url}
                    alt={project.title}
                    className="object-cover w-full h-full group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <h3 className="text-2xl font-bold mb-2">{project.title}</h3>
                  <p className="text-white/90 mb-4 line-clamp-2">
                    {project.description}
                  </p>
                  <div className="flex items-center gap-4 text-sm text-white/80">
                    <div className="flex items-center gap-1">
                      <Building2 className="w-4 h-4" />
                      <span>{project.category}</span>
                    </div>
                    {project.completion_date && (
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        <span>{new Date(project.completion_date).getFullYear()}</span>
                      </div>
                    )}
                    {project.location && (
                      <div className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        <span>{project.location}</span>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* Category Filter */}
      <div className="flex flex-wrap justify-center gap-4 mb-12">
        {categories.map((category) => (
          <button
            key={category}
            onClick={() => setSelectedCategory(category)}
            className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${selectedCategory === category
                ? 'bg-primary-600 text-white shadow-md'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
          >
            {category.charAt(0).toUpperCase() + category.slice(1)}
          </button>
        ))}
      </div>

      {/* Projects Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {filteredProjects.map((project) => (
          <motion.div
            key={project.id}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="group relative overflow-hidden rounded-2xl bg-white shadow-md hover:shadow-xl transition-all duration-300"
          >
            <div className="aspect-w-16 aspect-h-12 overflow-hidden">
              <img
                src={project.image_url}
                alt={project.title}
                className="object-cover w-full h-full group-hover:scale-110 transition-transform duration-300"
              />
            </div>
            <div className="p-6">
              <h3 className="text-xl font-bold mb-2">{project.title}</h3>
              <p className="text-gray-600 mb-4 line-clamp-2">
                {project.description}
              </p>
              <div className="flex items-center gap-4 text-sm text-gray-500">
                <div className="flex items-center gap-1">
                  <Building2 className="w-4 h-4" />
                  <span>{project.category}</span>
                </div>
                {project.completion_date && (
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    <span>{new Date(project.completion_date).getFullYear()}</span>
                  </div>
                )}
                {project.location && (
                  <div className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    <span>{project.location}</span>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* CTA Section */}
      <div className="mt-16 text-center">
        <h3 className="text-2xl font-bold mb-4">Ready to Start Your Project?</h3>
        <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
          Let's discuss your vision and create something extraordinary together.
        </p>
        <button
          onClick={openModal}
          className="btn-primary inline-flex items-center gap-2"
        >
          Contact Us
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </PageLayout>
  );
};

export default Projects;
